import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isApiError, kioskDeviceApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type {
  KioskRedReason,
  KioskScanResult,
  KioskSessionStatus,
} from "@bw/shared/types/generated";
import { useOnline } from "../../../common/hooks/deviceState";
import { playKioskChime } from "../lib/chime";
import {
  classifyExchangeFailure,
  exchangeFailureMessage,
} from "../lib/kioskResult";
import { runKioskScan } from "../lib/runScan";
import { createScanGate } from "../lib/scanGate";
import { parseSetupCode } from "../lib/setupCode";

/** Keep the kiosk listed as online at the desk (the server window is 120 s). */
export const KIOSK_HEARTBEAT_MS = 60_000;
/** While the server can't be reached on start, try again this often. */
export const KIOSK_BOOT_RETRY_MS = 5_000;
/** After a red, the same code is accepted again after this long (not 45 s). */
export const RED_RETRY_MS = 10_000;

export type KioskPhase = "booting" | "pairing" | "ready" | "unreachable";

export type KioskDisplay =
  | { kind: "green"; key: string; result: KioskScanResult }
  | { kind: "red"; key: string; reason: KioskRedReason }
  | { kind: "problem"; key: string; offline: boolean };

export const SIGNED_OUT_NOTICE =
  "This kiosk was signed out. Pair it again with a new setup code from Front desk › Devices.";

/**
 * The entrance kiosk's whole lifecycle (#2691): find the device session (or
 * pair with a setup code), scan with a debounce, show one green/red result at
 * a time, keep the device "online" at the desk, and return to pairing on any
 * 401 (revoked, expired). Mirrors the tournament venue-display capability:
 * the browser only ever holds an HttpOnly kiosk cookie set by the BFF.
 */
export function useKioskController({
  initialSetupCode = null,
}: { initialSetupCode?: string | null } = {}) {
  const queryClient = useQueryClient();
  const { signOut } = useAuth();
  const online = useOnline();
  const [phase, setPhase] = useState<KioskPhase>(initialSetupCode ? "pairing" : "booting");
  const [session, setSession] = useState<KioskSessionStatus | null>(null);
  const [pairingNotice, setPairingNotice] = useState<string | null>(null);
  const [pairingError, setPairingError] = useState<string | null>(null);
  const [pairing, setPairing] = useState(false);
  const [display, setDisplay] = useState<KioskDisplay | null>(null);
  const [checking, setChecking] = useState(false);
  const [unreachable, setUnreachable] = useState(false);
  const gate = useRef(createScanGate());
  const exchangeKeys = useRef(new Map<string, string>());
  // Setup codes the camera read that were refused. They are skipped until
  // staff tap Scan setup code again, so one used or expired QR in front of
  // the camera is not exchanged on every decoded frame.
  const failedSetupCodes = useRef(new Set<string>());
  const pairingRef = useRef(false);
  const [setupScanFailed, setSetupScanFailed] = useState(false);
  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const toPairing = useCallback((notice: string | null) => {
    gate.current.reset();
    setDisplay(null);
    setChecking(false);
    setSession(null);
    setPairingNotice(notice);
    setPhase("pairing");
  }, []);

  const loadSession = useCallback(async (): Promise<"ready" | "signed_out" | "unreachable"> => {
    try {
      const status = await kioskDeviceApi.getSession();
      setSession(status);
      setUnreachable(false);
      return "ready";
    } catch (error) {
      if (isApiError(error) && (error.status === 401 || error.status === 403)) {
        return "signed_out";
      }
      setUnreachable(true);
      return "unreachable";
    }
  }, []);

  // Boot: an existing device session goes straight to scanning.
  const booting = phase === "booting" || phase === "unreachable";
  useEffect(() => {
    if (!booting) return;
    let cancelled = false;
    let retry: number | undefined;
    const attempt = async () => {
      const outcome = await loadSession();
      if (cancelled) return;
      if (outcome === "ready") setPhase("ready");
      else if (outcome === "signed_out") toPairing(null);
      else {
        setPhase("unreachable");
        retry = window.setTimeout(() => void attempt(), KIOSK_BOOT_RETRY_MS);
      }
    };
    void attempt();
    return () => {
      cancelled = true;
      window.clearTimeout(retry);
    };
  }, [booting, loadSession, toPairing]);

  // Heartbeat while ready: keeps "online" at the desk and notices revocation.
  useEffect(() => {
    if (phase !== "ready") return;
    const timer = window.setInterval(() => {
      void loadSession().then((outcome) => {
        if (outcome === "signed_out" && phaseRef.current === "ready") {
          toPairing(SIGNED_OUT_NOTICE);
        }
      });
    }, KIOSK_HEARTBEAT_MS);
    return () => window.clearInterval(timer);
  }, [loadSession, phase, toPairing]);

  // Coming back online re-checks the session at once.
  useEffect(() => {
    if (online && phase === "ready" && unreachable) {
      void loadSession().then((outcome) => {
        if (outcome === "signed_out") toPairing(SIGNED_OUT_NOTICE);
      });
    }
  }, [loadSession, online, phase, toPairing, unreachable]);

  const pair = useCallback(
    async (raw: string, source: "camera" | "link") => {
      const code = parseSetupCode(raw);
      if (!code || pairingRef.current) return;
      pairingRef.current = true;
      setPairing(true);
      setPairingError(null);
      let key = exchangeKeys.current.get(code);
      if (!key) {
        key = crypto.randomUUID();
        exchangeKeys.current.set(code, key);
      }
      try {
        const start = await kioskDeviceApi.exchange({ setup_code: code, idempotency_key: key });
        // The BFF already deleted any staff/player session on this browser.
        // Clear the in-memory authority and every cached user-scoped response
        // too, exactly like the venue display exchange.
        queryClient.clear();
        try {
          await signOut();
        } catch {
          // Local sign-out state is cleared in its finally block.
        }
        queryClient.clear();
        gate.current.reset();
        setPairingNotice(null);
        const outcome = await loadSession();
        if (outcome === "signed_out") {
          toPairing(SIGNED_OUT_NOTICE);
          return;
        }
        if (outcome === "unreachable") {
          setSession({
            device_id: start.device_id,
            device_name: start.device_name,
            facility_id: start.facility_id,
            facility_name: start.facility_name,
            purposes: start.purposes,
            mode: "automatic",
            server_time: new Date().toISOString(),
            // Until the session loads, the tablet's own zone.
            facility_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });
        }
        setPhase("ready");
      } catch (error) {
        setPairingError(exchangeFailureMessage(classifyExchangeFailure(error)));
        if (source === "camera") {
          failedSetupCodes.current.add(code);
          setSetupScanFailed(true);
        }
      } finally {
        pairingRef.current = false;
        setPairing(false);
      }
    },
    [loadSession, queryClient, signOut, toPairing],
  );

  /** Staff tapped Scan setup code: codes that failed may be tried once more. */
  const retrySetupScan = useCallback(() => {
    failedSetupCodes.current.clear();
    setSetupScanFailed(false);
    setPairingError(null);
  }, []);

  const scanToken = useCallback(
    async (token: string) => {
      setChecking(true);
      const outcome = await runKioskScan({ token, scan: kioskDeviceApi.scan });
      setChecking(false);
      if (phaseRef.current !== "ready") return;
      if (outcome.type === "signed_out") {
        toPairing(SIGNED_OUT_NOTICE);
        return;
      }
      if (outcome.type === "off") {
        // Check-in was turned off: read the session now so "Check-in is off"
        // shows instead of a generic problem.
        gate.current.release();
        const status = await loadSession();
        if (status === "signed_out") toPairing(SIGNED_OUT_NOTICE);
        return;
      }
      if (outcome.type === "result") {
        const { result } = outcome;
        playKioskChime(result.outcome);
        setDisplay(
          result.outcome === "green" || !result.reason
            ? { kind: "green", key: result.scan_id, result }
            : { kind: "red", key: result.scan_id, reason: result.reason },
        );
        // A refused code may be fine once the player or desk fixes it (a
        // waiver signed, a booking found): accept it again sooner.
        if (result.outcome === "red") gate.current.retryAfter(token, Date.now(), RED_RETRY_MS);
        return;
      }
      if (outcome.type === "red") {
        gate.current.retryAfter(token, Date.now(), RED_RETRY_MS);
        playKioskChime("red");
        setDisplay({ kind: "red", key: crypto.randomUUID(), reason: outcome.reason });
        return;
      }
      setDisplay({
        kind: "problem",
        key: crypto.randomUUID(),
        offline: typeof navigator !== "undefined" && navigator.onLine === false,
      });
    },
    [loadSession, toPairing],
  );

  // A setup link (`/kiosk#code=…`) pairs as soon as the page opens.
  const pairedFromLink = useRef(false);
  useEffect(() => {
    if (!initialSetupCode || pairedFromLink.current) return;
    pairedFromLink.current = true;
    void pair(initialSetupCode, "link");
  }, [initialSetupCode, pair]);

  /** Every decoded camera frame lands here; the gate decides what counts. */
  const onDecode = useCallback(
    (text: string) => {
      if (phaseRef.current === "pairing") {
        const code = parseSetupCode(text);
        if (code && !failedSetupCodes.current.has(code)) void pair(code, "camera");
        return;
      }
      if (phaseRef.current !== "ready") return;
      if (session?.mode === "off") return;
      if (!gate.current.accept(text, Date.now())) return;
      void scanToken(text);
    },
    [pair, scanToken, session?.mode],
  );

  const dismissResult = useCallback(() => {
    setDisplay(null);
    gate.current.release();
  }, []);

  return {
    phase,
    session,
    display,
    checking,
    offline: !online || unreachable,
    pairing,
    pairingNotice,
    pairingError,
    setupScanFailed,
    retrySetupScan,
    onDecode,
    dismissResult,
  };
}
